import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Send, BadgeCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { name: '', email: '', phone: '', travel_dates: '', party_size: '', notes: '', subscribe_newsletter: false };

const RequestQuoteModal = ({ open, onClose, pkg }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill name/email/phone for logged-in customers (only if those fields are still empty)
  useEffect(() => {
    if (!open || !user || user.role !== 'customer') return;
    setForm((s) => ({
      ...s,
      name: s.name || user.name || '',
      email: s.email || user.email || '',
      phone: s.phone || user.phone || '',
    }));
    // Try to enrich phone from the traveler profile if not on the User object
    const token = localStorage.getItem('token');
    if (token && !user.phone) {
      axios
        .get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const phone = r.data?.phone;
          if (phone) setForm((s) => ({ ...s, phone: s.phone || phone }));
        })
        .catch(() => {});
    }
  }, [open, user]);

  if (!open) return null;

  const isMember = !!(user && user.role === 'customer');
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t('quote.errorRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/leads`,
        { package_id: pkg?.id, ...form },
        { headers },
      );
      toast.success(t('quote.success'));
      onClose();
      setForm(EMPTY_FORM);
    } catch {
      toast.error(t('quote.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quote-modal"
    >
      <div
        className="bg-cream rounded-sm border-2 border-maroon shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-maroon/10 sticky top-0 bg-cream">
          <div>
            <h2 className="text-2xl font-heading font-bold text-maroon">{t('quote.title')}</h2>
            {pkg?.title && <p className="text-sm text-maroon/60">{pkg.title}</p>}
          </div>
          <button onClick={onClose} className="text-maroon/50 hover:text-maroon p-1" aria-label="Close" data-testid="quote-close-btn">
            <X size={22} />
          </button>
        </div>

        {isMember && (
          <div className="mx-5 mt-4 flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-emerald-800 text-sm" data-testid="quote-member-banner">
            <BadgeCheck size={16} />
            <span>{t('quote.memberPrefilled', { name: user.name })}</span>
          </div>
        )}

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-maroon">{t('quote.name')} *</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} required className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none" data-testid="quote-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">{t('quote.email')} *</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none" data-testid="quote-email" />
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">{t('quote.phone')}</label>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none" data-testid="quote-phone" />
            </div>
            <div>
              <label className="text-sm font-semibold text-maroon">{t('quote.partySize')}</label>
              <input value={form.party_size} onChange={(e) => set('party_size', e.target.value)} placeholder="e.g., 2 adults" className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none" data-testid="quote-party-size" />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-maroon">{t('quote.travelDates')}</label>
            <input value={form.travel_dates} onChange={(e) => set('travel_dates', e.target.value)} placeholder="e.g., March 10–17, 2026" className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none" data-testid="quote-dates" />
          </div>

          <div>
            <label className="text-sm font-semibold text-maroon">{t('quote.notes')}</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={4} className="w-full mt-1 px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm focus:border-maroon focus:outline-none resize-none" placeholder={t('quote.notesPlaceholder')} data-testid="quote-notes" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-sm text-maroon">
            <input
              type="checkbox"
              checked={form.subscribe_newsletter}
              onChange={(e) => set('subscribe_newsletter', e.target.checked)}
              className="mt-1"
              data-testid="quote-subscribe-newsletter"
            />
            <span>Subscribe me to the monthly newsletter for travel inspiration & offers.</span>
          </label>

          <button type="submit" disabled={submitting} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="quote-submit-btn">
            <Send size={16} />
            {submitting ? t('quote.submitting') : t('quote.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestQuoteModal;
